import { useMemo, useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Sparkles, Wand2, Search, Plus, X, Download, MessageSquare } from 'lucide-react';

type PromptTemplate = {
  label: string;
  prompt: string;
};

const DEFAULT_PROMPTS: PromptTemplate[] = [
  {
    label: 'Summarize campaign risks',
    prompt:
      'Audit my campaign state and identify the top 3 narrative or pacing risks, with one concrete fix for each.',
  },
  {
    label: 'Generate session hook',
    prompt:
      'Generate 3 distinct session opening hooks tied to my current campaign context. Keep each hook concise and actionable.',
  },
  {
    label: 'Surface unused content',
    prompt:
      'Find underused NPCs, locations, and plot threads in my campaign and suggest how to reintroduce them next session.',
  },
];

const TAB_PROMPTS: Record<string, PromptTemplate[]> = {
  overview: [
    {
      label: 'GM pulse check',
      prompt:
        'Give me a pulse-check on this campaign: what is coherent, what is drifting, and what should I prioritize this week?',
    },
    {
      label: 'Top 5 priorities',
      prompt: 'List the top 5 campaign maintenance tasks I should complete before my next session.',
    },
  ],
  oracle: [
    {
      label: 'Regenerate Oracle',
      prompt:
        'Generate a fresh Oracle dynamic view focused on what matters most for my next session, with the top priorities first.',
    },
    {
      label: 'Explain relevance',
      prompt:
        'Explain why each Oracle panel is relevant right now and what specific action I should take for each.',
    },
  ],
  play: [
    {
      label: 'Live GM support',
      prompt:
        'I am in-session. Give me a fast GM support checklist for pacing, spotlight balance, and consequence tracking.',
    },
    {
      label: 'Emergency improv',
      prompt:
        'Players are off-script. Give me 3 improv branches that stay consistent with current campaign context.',
    },
  ],
  sessions: [
    {
      label: 'Prep checklist',
      prompt:
        'Build a prep checklist for my next session from active threads, key NPCs, and unresolved outcomes.',
    },
    {
      label: 'Recap draft',
      prompt:
        'Draft a concise recap for players and a separate GM-only note with hidden implications.',
    },
  ],
  characters: [
    {
      label: 'Spotlight moments',
      prompt:
        'Suggest one meaningful spotlight moment for each active player character in the next session.',
    },
    {
      label: 'Character tension',
      prompt: 'Suggest respectful intra-party tension hooks that deepen roleplay without derailing play.',
    },
  ],
  npcs: [
    {
      label: 'NPC depth pass',
      prompt:
        'Choose 3 NPCs and add one secret, one motive pressure, and one playable mannerism for each.',
    },
    {
      label: 'NPC reaction web',
      prompt:
        'Map likely reactions across key NPCs if the party fails their next objective.',
    },
  ],
  locations: [
    {
      label: 'Location hooks',
      prompt:
        'Generate 5 short scene hooks tied to existing locations, each with a clear narrative purpose.',
    },
    {
      label: 'Travel complications',
      prompt:
        'Propose travel complications between current key locations, balanced across combat/social/exploration.',
    },
  ],
  plots: [
    {
      label: 'Plot convergence',
      prompt: 'Propose 3 ways to converge active plot threads without invalidating previous player choices.',
    },
    {
      label: 'Twist pass',
      prompt: 'Suggest one fair foreshadowed twist for each high-priority active plot thread.',
    },
  ],
  wiki: [
    {
      label: 'Codex cleanup',
      prompt:
        'Identify missing cross-links and weak summaries in the wiki, and suggest exact improvements.',
    },
    {
      label: 'Lore consistency',
      prompt:
        'Run a lore consistency pass and highlight contradictions or ambiguous facts to resolve.',
    },
  ],
  encounters: [
    {
      label: 'Encounter variants',
      prompt:
        'Give me 3 encounter variants for the current campaign tone: easy pressure, tactical hard, and narrative set-piece.',
    },
    {
      label: 'Counterplay options',
      prompt:
        'For my likely next encounter, suggest player-visible counterplay options to keep challenge fair and strategic.',
    },
  ],
  rules: [
    {
      label: 'Rules handout',
      prompt: 'Create a player-facing quick reference from current high-use compendium rules.',
    },
    {
      label: 'Rule conflicts',
      prompt:
        'Scan current compendium usage patterns and call out possible rule conflicts or interpretation gaps.',
    },
  ],
  sheets: [
    {
      label: 'Build audit',
      prompt:
        'Audit current character sheets for missing runtime data and suggest high-impact completion tasks.',
    },
    {
      label: 'Resource tuning',
      prompt:
        'Recommend resource pressures for next session based on sheet levels and party composition.',
    },
  ],
  publish: [
    {
      label: 'Publication plan',
      prompt:
        'Draft a publish plan for this week: what to release publicly, what to keep draft, and why.',
    },
    {
      label: 'Reader UX pass',
      prompt:
        'Recommend improvements to public route ordering, naming, and teaser copy for better readability.',
    },
  ],
  memory: [
    {
      label: 'Ingestion gaps',
      prompt:
        'Identify what campaign source material is missing from assistant memory and prioritize ingestion order.',
    },
    {
      label: 'Question set',
      prompt:
        'Create 10 high-value questions I should ask memory right now to verify retrieval quality.',
    },
  ],
};

interface CampaignStats {
  npcCount?: number;
  locationCount?: number;
  activePlotCount?: number;
  sessionNumber?: number;
  partyHeat?: number;
}

interface AiFABProps {
  activeTab: string;
  activeTabLabel: string;
  isBusy: boolean;
  isOffline?: boolean;
  offlineQueueCount?: number;
  isQueueSyncing?: boolean;
  campaignStats?: CampaignStats;
  onSyncQueue?: () => void;
  onSendPrompt: (prompt: string) => void;
  onOpenAssistant: () => void;
  onOpenSearch: () => void;
  onOpenQuickAdd: () => void;
  onExportChat?: (format: 'markdown' | 'json') => void;
}

function buildDynamicPrompts(stats: CampaignStats | undefined, activeTab: string): PromptTemplate[] {
  if (!stats) return [];
  const dynamic: PromptTemplate[] = [];

  if (stats.partyHeat != null && stats.partyHeat >= 4) {
    dynamic.push({
      label: 'Heat management',
      prompt: `Party heat is at ${stats.partyHeat}/5. What consequences should I introduce, and how can the party manage their heat level?`,
    });
  }

  if (stats.npcCount != null && stats.npcCount < 3 && (activeTab === 'npcs' || activeTab === 'overview')) {
    dynamic.push({
      label: 'Create key NPCs',
      prompt: 'My campaign needs more NPCs. Help me create 3 key NPCs that fill different roles (ally, neutral, antagonist) and tie into my active plot threads.',
    });
  }

  if (stats.activePlotCount != null && stats.activePlotCount >= 4) {
    dynamic.push({
      label: 'Converge plot threads',
      prompt: `I have ${stats.activePlotCount} active plot threads. Which ones could naturally converge, and how can I start weaving them together?`,
    });
  }

  if (stats.sessionNumber != null && stats.sessionNumber > 0 && (activeTab === 'overview' || activeTab === 'sessions')) {
    dynamic.push({
      label: `Prep session ${stats.sessionNumber + 1}`,
      prompt: `Help me prepare for session ${stats.sessionNumber + 1}. What loose ends from session ${stats.sessionNumber} need follow-up? What hooks should I prepare?`,
    });
  }

  return dynamic.slice(0, 2);
}

export default function AiFAB({
  activeTab,
  activeTabLabel,
  isBusy,
  isOffline = false,
  offlineQueueCount = 0,
  isQueueSyncing = false,
  campaignStats,
  onSyncQueue,
  onSendPrompt,
  onOpenAssistant,
  onOpenSearch,
  onOpenQuickAdd,
  onExportChat,
}: AiFABProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [promptInput, setPromptInput] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const tabPrompts = useMemo(() => {
    const base = TAB_PROMPTS[activeTab]?.length ? TAB_PROMPTS[activeTab] : DEFAULT_PROMPTS;
    const dynamic = buildDynamicPrompts(campaignStats, activeTab);
    return [...dynamic, ...base].slice(0, 4);
  }, [activeTab, campaignStats]);

  const submitPrompt = (prompt: string) => {
    const trimmed = prompt.trim();
    if (!trimmed || isBusy) return;
    onSendPrompt(trimmed);
    setPromptInput('');
    setIsOpen(false);
  };

  // Hide FAB when already on the assistant workspace tab
  if (activeTab === 'assistant') return null;

  return (
    <div className="fixed bottom-6 right-6 z-[90]" ref={popoverRef}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="ai-fab-popover"
          >
            <div className="ai-fab-popover-header">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-[var(--accent)]" />
                <span className="text-sm font-semibold text-[var(--text-primary)]">
                  Quick Command
                </span>
                <span className="text-xs text-[var(--text-muted)]">&mdash; {activeTabLabel}</span>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex gap-2 mb-3">
              <input
                value={promptInput}
                data-testid="ai-dock-input"
                onChange={(event) => setPromptInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    submitPrompt(promptInput);
                  }
                }}
                placeholder={`Ask AI for help in ${activeTabLabel.toLowerCase()}...`}
                className="flex-1 rounded-md border border-[var(--border-default)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)]"
                autoFocus
              />
              <button
                type="button"
                data-testid="ai-dock-submit"
                onClick={() => submitPrompt(promptInput)}
                disabled={!promptInput.trim() || isBusy}
                className={`btn-primary-solid ${!promptInput.trim() || isBusy ? 'opacity-60 pointer-events-none' : ''}`}
              >
                <Wand2 size={14} />
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-3">
              {tabPrompts.map((template) => (
                <button
                  key={template.label}
                  type="button"
                  onClick={() => submitPrompt(template.prompt)}
                  disabled={isBusy}
                  className="rounded-full border border-[var(--border-default)] bg-[var(--bg-tertiary)] px-2.5 py-1 text-[11px] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-60 whitespace-normal text-left leading-tight"
                  title={template.prompt}
                >
                  {template.label}
                </button>
              ))}
            </div>

            <div className="ai-fab-actions">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  onOpenAssistant();
                }}
                className="ai-fab-action-btn"
              >
                <MessageSquare size={12} />
                Open Workspace
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  onOpenSearch();
                }}
                className="ai-fab-action-btn"
              >
                <Search size={12} />
                Search
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  onOpenQuickAdd();
                }}
                className="ai-fab-action-btn"
              >
                <Plus size={12} />
                Quick Add
              </button>
              {onExportChat && (
                <button
                  type="button"
                  onClick={() => onExportChat('markdown')}
                  className="ai-fab-action-btn"
                  title="Export chat as Markdown"
                >
                  <Download size={12} />
                  Export
                </button>
              )}
              {offlineQueueCount > 0 && (
                <div className="flex items-center gap-1.5 ml-auto">
                  <span
                    data-testid="assistant-queue-badge"
                    className="text-[10px] text-[var(--text-muted)]"
                  >
                    Queued: {offlineQueueCount}
                  </span>
                  {onSyncQueue && (
                    <button
                      type="button"
                      data-testid="assistant-queue-sync-dock"
                      onClick={onSyncQueue}
                      disabled={isOffline || isQueueSyncing}
                      className="text-[10px] text-[var(--secondary-text)] hover:underline disabled:opacity-50"
                    >
                      {isQueueSyncing ? 'Syncing...' : 'Sync'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="ai-fab"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        title="AI Quick Command"
      >
        <Sparkles size={22} />
        <span className="ai-fab-label">AI</span>
        {isBusy && (
          <motion.div
            className="absolute inset-0 rounded-full border border-[var(--accent-glow)]"
            animate={{ opacity: [0.3, 0.8, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}
      </motion.button>
    </div>
  );
}
